import { useState, useRef, useEffect } from "react";
import { joinNetwork, submitContact, findLegislator, getPriorities } from "@/lib/api";
import { toast } from "sonner";
import { CaptchaWidget } from "@/components/public/CaptchaWidget";
import { useT } from "@/lib/t";
import { useLang } from "@/context/LangContext";

// ────────────────────────────────────────────────────────────
// Join the Network
// ────────────────────────────────────────────────────────────
export function JoinNetwork() {
  const t = useT();
  const { lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", zip: "", parish: "", interests: [] });
  const [priorities, setPriorities] = useState([]);
  const [done, setDone] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  useEffect(() => { getPriorities(lang).then(setPriorities); }, [lang]);

  const toggle = (slug) => {
    setForm(f => ({ ...f, interests: f.interests.includes(slug) ? f.interests.filter(x => x !== slug) : [...f.interests, slug] }));
  };
  const submit = async (e) => {
    e.preventDefault();
    if (captchaRef.current?.isEnabled() && !captchaToken) return toast.error(t("misc.captcha_required"));
    try { await joinNetwork({ ...form, source: "join-page" }, captchaToken); setDone(true); toast.success(t("join.toast_welcome")); }
    catch (err) {
      toast.error(err?.response?.data?.detail || t("misc.generic_fail"));
      captchaRef.current?.reset();
      setCaptchaToken(null);
    }
  };

  return (
    <div data-testid="join-network-page">
      <section className="bg-ccc-plum text-ccc-paper py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="eyebrow eyebrow-light mb-3">{t("join.eyebrow")}</div>
          <h1 className="font-serif text-5xl mb-4">{t("join.hero_title")}</h1>
          <p className="text-ccc-paper/85 max-w-2xl" style={{ fontSize: "18px", lineHeight: 1.7 }}>{t("join.hero_body")}</p>
        </div>
      </section>
      <section className="max-w-3xl mx-auto px-6 py-16">
        {done ? (
          <div className="bg-white border border-ccc-rule border-l-4 border-l-ccc-gold p-8 text-center">
            <div className="font-serif text-3xl text-ccc-plum mb-2">{t("join.welcome_title")}</div>
            <p className="text-ccc-body" style={{ fontSize: "17px" }}>{t("join.welcome_body")}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-ccc-rule p-8 grid grid-cols-2 gap-4">
            <input required placeholder={t("join.full_name")} value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              data-testid="jn-name"
              className="col-span-2 px-4 py-3 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
              style={{ fontSize: "17px" }}/>
            <input required type="email" placeholder={t("join.email")} value={form.email} onChange={e => setForm({...form, email: e.target.value})}
              data-testid="jn-email"
              className="col-span-2 px-4 py-3 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
              style={{ fontSize: "17px" }}/>
            <input placeholder={t("join.zip")} value={form.zip} onChange={e => setForm({...form, zip: e.target.value})}
              className="px-4 py-3 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
              style={{ fontSize: "17px" }}/>
            <input placeholder={t("join.parish")} value={form.parish} onChange={e => setForm({...form, parish: e.target.value})}
              className="px-4 py-3 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
              style={{ fontSize: "17px" }}/>
            <div className="col-span-2">
              <div className="text-ccc-body mb-2" style={{ fontSize: "15px" }}>{t("join.interests_label")}</div>
              <div className="flex flex-wrap gap-2">
                {priorities.map(p => (
                  <button type="button" key={p.slug} onClick={() => toggle(p.slug)}
                    className={`px-3 py-1 uppercase tracking-wider border transition-colors ${form.interests.includes(p.slug) ? "bg-ccc-plum text-white border-ccc-plum" : "border-ccc-rule text-ccc-body hover:border-ccc-plum"}`}
                    style={{ fontSize: "13px" }}>
                    {p.title}
                  </button>
                ))}
              </div>
            </div>
            <button className="col-span-2 mt-3 bg-ccc-plum text-ccc-paper py-3 uppercase tracking-[0.08em] font-semibold hover:bg-ccc-plum-deep" data-testid="jn-submit" style={{ fontSize: "15px" }}>
              {t("join.submit")}
            </button>
            <div className="col-span-2"><CaptchaWidget ref={captchaRef} onToken={setCaptchaToken} /></div>
          </form>
        )}
      </section>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Contact
// ────────────────────────────────────────────────────────────
export function Contact() {
  const t = useT();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [done, setDone] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const submit = async e => {
    e.preventDefault();
    if (captchaRef.current?.isEnabled() && !captchaToken) return toast.error(t("misc.captcha_required"));
    try { await submitContact(form, captchaToken); setDone(true); }
    catch (err) {
      toast.error(err?.response?.data?.detail || t("misc.generic_fail"));
      captchaRef.current?.reset();
      setCaptchaToken(null);
    }
  };
  return (
    <div data-testid="contact-page" className="max-w-3xl mx-auto px-6 py-16">
      <div className="eyebrow mb-3">{t("contact.eyebrow")}</div>
      <h1 className="font-serif text-5xl text-ccc-plum mb-6">{t("contact.hero_title")}</h1>
      <p className="text-ccc-body mb-8" style={{ fontSize: "17px" }}>{t("contact.address_line")}</p>
      {done ? (
        <div className="bg-white border border-ccc-rule border-l-4 border-l-ccc-gold p-8">
          <div className="font-serif text-2xl text-ccc-plum">{t("contact.thanks")}</div>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-white border border-ccc-rule p-8 grid grid-cols-2 gap-4">
          <input required placeholder={t("contact.name")} value={form.name} onChange={e => setForm({...form, name: e.target.value})}
            data-testid="contact-name"
            className="col-span-2 px-4 py-3 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
            style={{ fontSize: "17px" }}/>
          <input required type="email" placeholder={t("contact.email")} value={form.email} onChange={e => setForm({...form, email: e.target.value})}
            data-testid="contact-email"
            className="col-span-2 px-4 py-3 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
            style={{ fontSize: "17px" }}/>
          <input required placeholder={t("contact.subject")} value={form.subject} onChange={e => setForm({...form, subject: e.target.value})}
            data-testid="contact-subject"
            className="col-span-2 px-4 py-3 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
            style={{ fontSize: "17px" }}/>
          <textarea required rows={7} placeholder={t("contact.message")} value={form.message} onChange={e => setForm({...form, message: e.target.value})}
            data-testid="contact-message"
            className="col-span-2 px-4 py-3 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
            style={{ fontSize: "17px" }}/>
          <div className="col-span-2"><CaptchaWidget ref={captchaRef} onToken={setCaptchaToken} /></div>
          <button className="col-span-2 bg-ccc-plum text-ccc-paper py-3 uppercase tracking-[0.08em] font-semibold hover:bg-ccc-plum-deep" data-testid="contact-submit" style={{ fontSize: "15px" }}>
            {t("contact.submit")}
          </button>
        </form>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Find My Legislator
// ────────────────────────────────────────────────────────────
export function FindMyLegislator() {
  const t = useT();
  const [zip, setZip] = useState("");
  const [result, setResult] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    try { setResult(await findLegislator({ zip })); } catch { toast.error(t("misc.generic_fail")); }
  };
  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="find-legislator-page">
      <div className="eyebrow mb-3">{t("legislator.eyebrow")}</div>
      <h1 className="font-serif text-5xl text-ccc-plum mb-4">{t("legislator.hero_title")}</h1>
      <p className="text-ccc-body mb-6" style={{ fontSize: "17px" }}>{t("legislator.hero_body")}</p>
      <form onSubmit={submit} className="flex gap-2 mb-8">
        <input value={zip} onChange={e => setZip(e.target.value)} placeholder={t("legislator.zip_placeholder")} required
          className="flex-1 px-4 py-3 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
          style={{ fontSize: "17px" }}/>
        <button className="bg-ccc-plum text-ccc-paper px-6 py-3 uppercase tracking-[0.08em] font-semibold hover:bg-ccc-plum-deep" style={{ fontSize: "15px" }}>
          {t("legislator.submit")}
        </button>
      </form>
      {result && (
        <div className="space-y-3" data-testid="legislator-results">
          {result.legislators.map(l => (
            <div key={l.id} className="bg-white border border-ccc-rule p-5">
              <div className="eyebrow" style={{ fontSize: "12px" }}>{l.chamber} &middot; {l.district}</div>
              <div className="font-serif text-2xl text-ccc-plum">{l.name}</div>
              <div className="text-ccc-body mt-1" style={{ fontSize: "15px" }}>{l.email} &middot; {l.phone}</div>
              <div className="text-ccc-muted mt-2" style={{ fontSize: "13px" }}>{t("legislator.committees_label")}: {l.committees.join(", ")}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Jobs
// ────────────────────────────────────────────────────────────
export function Jobs() {
  const t = useT();
  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="jobs-page">
      <div className="eyebrow mb-3">{t("jobs.eyebrow")}</div>
      <h1 className="font-serif text-5xl text-ccc-plum mb-6">{t("jobs.hero_title")}</h1>
      <div className="bg-white border border-ccc-rule p-8">
        <p className="text-ccc-body leading-relaxed" style={{ fontSize: "17px" }}>
          {t("jobs.no_openings_prefix")}
          <a href="mailto:info@cocatholic.org" className="text-ccc-plum underline decoration-ccc-gold underline-offset-4">info@cocatholic.org</a>.
        </p>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Resources index
// ────────────────────────────────────────────────────────────
export function Resources() {
  const t = useT();
  const cards = [
    { to: "/resources/statements", title: t("resources.card_statements_title"), desc: t("resources.card_statements_desc") },
    { to: "/resources/bishop-letters", title: t("resources.card_letters_title"), desc: t("resources.card_letters_desc") },
    { to: "/resources/voter-guide", title: t("resources.card_voter_title"), desc: t("resources.card_voter_desc") },
  ];
  return (
    <div className="max-w-6xl mx-auto px-6 py-16" data-testid="resources-page">
      <div className="eyebrow mb-3">{t("resources.eyebrow")}</div>
      <h1 className="font-serif text-5xl text-ccc-plum mb-10">{t("resources.hero_title")}</h1>
      <div className="grid md:grid-cols-3 gap-6">
        {cards.map(c => (
          <a key={c.to} href={c.to} className="bg-white border border-ccc-rule p-6 hover:border-ccc-plum transition-colors block">
            <div className="font-serif text-2xl text-ccc-plum mb-2">{c.title}</div>
            <div className="text-ccc-body" style={{ fontSize: "16px" }}>{c.desc}</div>
          </a>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Bishops' Letters
// ────────────────────────────────────────────────────────────
export function BishopLetters() {
  const t = useT();
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => {
    import("@/lib/api").then(mod => mod.getStatements({ type: "Bishop Letter", lang }).then(setItems));
  }, [lang]);
  return (
    <div className="max-w-4xl mx-auto px-6 py-16" data-testid="bishop-letters-page">
      <div className="eyebrow mb-3">{t("bishop_letters.eyebrow")}</div>
      <h1 className="font-serif text-5xl text-ccc-plum mb-8">{t("bishop_letters.hero_title")}</h1>
      <div className="space-y-4">
        {items.map(s => (
          <a key={s.id} href={`/resources/statements/${s.slug}`} className="block bg-white border border-ccc-rule p-6 hover:border-ccc-plum">
            <div className="eyebrow mb-2" style={{ fontSize: "13px" }}>{s.published_at}</div>
            <div className="font-serif text-2xl text-ccc-plum">{s.title}</div>
            <p className="text-ccc-body mt-2" style={{ fontSize: "16px" }}>{s.excerpt}</p>
          </a>
        ))}
        {items.length === 0 && <div className="text-ccc-muted" style={{ fontSize: "17px" }}>{t("bishop_letters.empty")}</div>}
      </div>
    </div>
  );
}
